/**
 * The workspace every discipline shares: back, the project's name, the discipline
 * switch and the account menu on one row; the grouped tabs and the screen's tools on
 * the next; a rail in the discipline's colour above both.
 *
 * Header and tabs are ONE sticky block. They used to be two, the second stuck at a
 * hard-coded 53px - which was right only while the header was exactly that tall.
 */

import type { ReactNode } from 'react';
import clsx from 'clsx';
import { AccountMenuV2 } from '../AccountMenuV2';
import type { Discipline } from '../disciplines';
import { Button } from './Button';

export interface ShellTab {
  id: string;
  label: string;
  disabled?: boolean;
}

export interface ShellGroup {
  id: string;
  /** Shown above its tabs; empty for a trailing group nobody needs named. */
  label: string;
  tabs: ShellTab[];
}

export function WorkspaceShell({
  discipline,
  backLabel = 'Projects',
  onBack,
  title,
  subtitle,
  status,
  switcher,
  groups,
  active,
  onSelect,
  tools,
  banner,
  aside,
  children,
}: {
  discipline: Discipline;
  backLabel?: string;
  onBack: () => void;
  title: string;
  subtitle?: ReactNode;
  status?: ReactNode;
  switcher?: ReactNode;
  groups: ShellGroup[];
  active: string;
  onSelect: (id: string) => void;
  tools?: ReactNode;
  banner?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div data-discipline={discipline.id} className="flex min-h-screen min-w-0 flex-col bg-canvas">
      <div className="sticky top-0 z-20 shrink-0 border-b border-gray-200 bg-white shadow-[0_1px_2px_rgb(var(--c-black)/0.03)]">
        <div aria-hidden="true" className="h-[3px] bg-accent-600" />
        <div className="flex items-center gap-3 px-4 py-2.5">
          <Button size="sm" onClick={onBack}>
            <span aria-hidden="true">&larr;</span>
            {backLabel}
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <div className="truncate font-display text-base font-bold text-gray-900">{title}</div>
              {status}
            </div>
            {subtitle && <div className="truncate text-xs text-gray-500">{subtitle}</div>}
          </div>
          {switcher && <div className="ml-2 hidden md:block">{switcher}</div>}
          {/* The screen's tools sit with the project's controls, not in the tab row:
              the tabs need the whole width, and IAS accounting has nineteen. */}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {tools && <div className="hidden items-center gap-2 lg:flex">{tools}</div>}
            <AccountMenuV2 />
          </div>
        </div>
        {tools && <div className="flex items-center gap-2 px-4 pb-2 lg:hidden">{tools}</div>}
        <div className="px-4">
          <nav aria-label="Tabs" className="v2-tabstrip -mb-px flex items-end gap-1 overflow-x-auto">
            {groups.map((group, index) => (
              <div key={group.id} className="flex">
                {index > 0 && <div aria-hidden="true" className="mx-2 mb-2 mt-5 w-px shrink-0 self-stretch bg-gray-200" />}
                <div className="flex flex-col">
                  <span className="h-4 px-3 text-[10px] font-semibold uppercase leading-4 tracking-wider text-gray-400">
                    {group.label}
                  </span>
                  <div className="flex gap-0.5">
                    {group.tabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        disabled={tab.disabled}
                        aria-current={active === tab.id ? 'page' : undefined}
                        onClick={() => onSelect(tab.id)}
                        className={clsx(
                          'whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors',
                          tab.disabled
                            ? 'cursor-not-allowed border-transparent text-gray-300'
                            : active === tab.id
                              ? 'border-accent-600 font-semibold text-accent-700'
                              : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900',
                        )}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </nav>
        </div>
      </div>
      {banner}
      <div className="flex min-h-0 flex-1">
        <main className="min-w-0 flex-1">{children}</main>
        {aside}
      </div>
    </div>
  );
}
